import React, { useState, useEffect } from 'react';
import { 
  DocumentDuplicateIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShareIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { templateService, Template, TemplateFilters } from '../services/template';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../utils/toast';
import { TemplateModal } from '../components/TemplateModal';

export const Templates: React.FC = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [filters, setFilters] = useState<TemplateFilters>({
    page: 1,
    limit: 20,
    search: '',
    type: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20
  });
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const isAdmin = user?.role === 'admin';
  const isProvider = user?.role === 'operator' || user?.role === 'admin';

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const result = await templateService.getTemplates(filters);
      setTemplates(result.templates);
      setCategories(result.categories);
      setTags(result.tags);
      setPagination({
        total: result.total,
        page: result.page,
        limit: result.limit
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handleFilterChange = (key: keyof TemplateFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    if (!template.canEdit) {
      toast.error('You do not have permission to edit this template');
      return;
    }
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleModalSuccess = () => {
    fetchTemplates();
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      const newName = `${template.name} (Copy)`;
      await templateService.duplicateTemplate(template.id, newName);
      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.message || 'Failed to duplicate template');
    }
  };

  const handleShareTemplate = async (template: Template) => {
    try {
      if (template.isShared) {
        toast('This template has already been shared and converted to an organization template. You can delete your personal copy, but the organization template will remain.', { icon: 'ℹ️' });
        return;
      } else {
        if (confirm(`Share "${template.name}" with your organization?\n\nThis will create a new organization template that all members can use. Your personal template will remain as a reference.`)) {
          await templateService.shareTemplate(template.id);
          toast.success('Template shared with organization and converted to organization template');
          fetchTemplates();
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to share template');
    }
  };

  const handleDeleteTemplate = async (template: Template) => {
    if (!template.canEdit) {
      toast.error('You do not have permission to delete this template');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await templateService.deleteTemplate(template.id);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete template');
    }
  };

  const getAccessLevelBadge = (template: Template) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (template.accessLevel) {
      case 'public':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'organization':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'shared':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'private':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeBadge = (type: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (type) {
      case 'community':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'organization':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'personal':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (!isProvider) {
    return (
      <div className="text-center py-8">
        <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-500">Only providers and administrators can access templates.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Template Management</h2>
          <p className="text-sm text-gray-500">
            Manage transcription templates for clinical documentation
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCreateTemplate}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search templates..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filters.type || 'all'}
              onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Types</option>
              <option value="community">Community</option>
              <option value="organization">Organization</option>
              <option value="personal">Personal</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={filters.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={filters.sortBy || 'name'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="name">Name</option>
              <option value="createdAt">Created Date</option>
              <option value="usageCount">Usage Count</option>
              <option value="lastUsed">Last Used</option>
            </select>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <DocumentDuplicateIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Found</h3>
              <p className="text-gray-500">Create your first template to get started.</p>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-200">
              {templates.map((template) => (
                <li key={template.id} className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <DocumentIcon className="h-8 w-8 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {template.name}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {template.description}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={getTypeBadge(template.type)}>
                              {template.type}
                            </span>
                            <span className={getAccessLevelBadge(template)}>
                              {template.accessLevel}
                            </span>
                            <span className="text-xs text-gray-500">
                              Category: {template.category}
                            </span>
                            <span className="text-xs text-gray-500">
                              Used {template.usageCount} times
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDuplicateTemplate(template)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Duplicate Template"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      
                      {template.type === 'personal' && (
                        <button
                          onClick={() => handleShareTemplate(template)}
                          className={`${template.isShared ? 'text-purple-600 hover:text-purple-800' : 'text-gray-400 hover:text-gray-600'}`}
                          title={template.isShared ? 'Already Shared (Cannot Unshare)' : 'Share Template with Organization'}
                        >
                          <ShareIcon className="h-4 w-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleEditTemplate(template)}
                        disabled={!template.canEdit}
                        className={`${template.canEdit ? 'text-indigo-600 hover:text-indigo-900' : 'text-gray-300 cursor-not-allowed'}`}
                        title="Edit Template"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      
                      {template.canEdit && template.type !== 'community' && (
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Template"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Template Modal */}
      <TemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        template={selectedTemplate}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};